'use client'

import { useLocale } from 'next-intl'

import {
  type DateInput,
  DEFAULT_FALLBACK,
  formatInstant
} from './formatDateTime'
import type { DateTimePreset } from './presets'
import { useIsHydrated } from './useIsHydrated'

type LocalDateTimeProps = {
  value: DateInput
  preset: DateTimePreset
  fallback?: string
  className?: string
  /** Override UI locale when design requires a fixed date shape. */
  formatLocale?: string
}

function toIso(value: DateInput): string | undefined {
  if (value == null) {
    return undefined
  }
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString()
}

/**
 * Renders a server UTC instant in the viewer's local timezone. Before hydration
 * it formats in UTC (deterministic, matches the SSR markup), then re-renders in
 * the browser zone after mount. Use for instants like completed_at/joined_at.
 * For calendar dates (news_date, i18n strings) use formatCalendarDate instead.
 * OCE-1226.
 */
export function LocalDateTime({
  value,
  preset,
  fallback = DEFAULT_FALLBACK,
  className,
  formatLocale
}: LocalDateTimeProps) {
  const uiLocale = useLocale()
  const locale = formatLocale ?? uiLocale
  const hydrated = useIsHydrated()
  const label = formatInstant(value, {
    locale,
    preset,
    timeZone: hydrated ? undefined : 'UTC',
    fallback
  })
  return (
    <time
      dateTime={toIso(value)}
      className={className}
      suppressHydrationWarning
    >
      {label}
    </time>
  )
}
